import React, { useState, useEffect, useRef } from 'react';
import { billsAPI } from '../services/api';
import ApiSelect from '../components/ApiSelect';
import FilterSection from '../components/FilterSection';
import { useToast } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import { SkeletonTable, SkeletonCards } from '../components/Skeleton';
import FYPicker from '../components/FYPicker';
import { getCurrentFY, getFYLabel, getAdjacentFYs } from '../utils/financialYear';

const Bills = () => {
  const toast = useToast();
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, id: null });
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [formData, setFormData] = useState({
    company: null,
    party: null,
    billNumber: '',
    amount: '',
    billDate: new Date().toISOString().split('T')[0], // Default to today
    financialYear: getCurrentFY()
  });

  const [selectedFY, setSelectedFY] = useState(getCurrentFY());

  // Single filter state (like Reports page)
  const [filters, setFilters] = useState({
    company: null,
    party: null,
    billNumber: ''
  });

  // Separate state for bill number input (for debouncing)
  const [billNumberInput, setBillNumberInput] = useState('');
  const billNumberTimeoutRef = useRef(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 15,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Combined effect with debounce for bill number search, immediate for other filters and pagination
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchBills();
    }, filters.billNumber ? 1000 : 0); // Debounce only for bill number search, immediate for others

    return () => clearTimeout(timeoutId);
  }, [filters, pagination.currentPage, selectedFY]);

  // Reset pagination when filters change
  useEffect(() => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, [filters]);

  const fetchBills = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage
      };

      if (filters.company) params.company = filters.company._id;
      if (filters.party) params.party = filters.party._id;
      if (filters.billNumber) params.billNumber = filters.billNumber;
      if (selectedFY) params.fy = selectedFY;

      const response = await billsAPI.getAll(params);
      setBills(response.data.bills);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching bills:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      // Auto-compute FY when billDate changes
      if (name === 'billDate' && value) {
        updated.financialYear = getFYLabel(value);
      }
      return updated;
    });
  };

  const handleCompanySelect = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      company: selectedOption ? selectedOption.data : null
    }));
  };

  const handlePartySelect = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      party: selectedOption ? selectedOption.data : null
    }));
  };

  const handleCompanyFilterSelect = (selectedOption) => {
    const company = selectedOption ? selectedOption.data : null;
    setFilters(prev => ({
      ...prev,
      company: company
    }));
  };

  const handlePartyFilterSelect = (selectedOption) => {
    const party = selectedOption ? selectedOption.data : null;
    setFilters(prev => ({
      ...prev,
      party: party
    }));
  };

  const handleBillNumberFilterChange = (e) => {
    const value = e.target.value;
    setBillNumberInput(value);

    // Clear existing timeout
    if (billNumberTimeoutRef.current) {
      clearTimeout(billNumberTimeoutRef.current);
    }

    // Set new timeout for debounced filter update
    billNumberTimeoutRef.current = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        billNumber: value
      }));
    }, 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.company || !formData.party || !formData.billNumber || !formData.amount || !formData.billDate) {
      toast.error('All fields are required');
      return;
    }

    try {
      const billData = {
        company: formData.company._id,
        party: formData.party._id,
        billNumber: formData.billNumber,
        amount: parseFloat(formData.amount),
        billDate: formData.billDate,
        financialYear: formData.financialYear
      };

      if (editingBill) {
        await billsAPI.update(editingBill._id, billData);
      } else {
        await billsAPI.create(billData);
      }

      toast.success(editingBill ? 'Bill updated' : 'Bill created');
      resetForm();
      fetchBills(); // Refresh after create/update
    } catch (error) {
      console.error('Error saving bill:', error);
      toast.error('Error saving bill');
    }
  };

  const handleEdit = (bill) => {
    setEditingBill(bill);
    setFormData({
      company: bill.company,
      party: bill.party,
      billNumber: bill.billNumber,
      amount: bill.amount.toString(),
      billDate: new Date(bill.billDate).toISOString().split('T')[0],
      financialYear: bill.financialYear || getFYLabel(bill.billDate)
    });
    setShowForm(true);
  };

  const handleDelete = (id) => {
    setConfirmDialog({ isOpen: true, id });
  };

  const confirmDelete = async () => {
    try {
      await billsAPI.delete(confirmDialog.id);
      toast.success('Bill deleted');
      fetchBills();
    } catch (error) {
      console.error('Error deleting bill:', error);
      toast.error('Error deleting bill');
    } finally {
      setConfirmDialog({ isOpen: false, id: null });
    }
  };

  const resetForm = () => {
    setFormData({
      company: null,
      party: null,
      billNumber: '',
      amount: '',
      billDate: new Date().toISOString().split('T')[0],
      financialYear: getCurrentFY()
    });
    setEditingBill(null);
    setShowForm(false);
  };

  const clearFilters = () => {
    setFilters({
      company: null,
      party: null,
      billNumber: ''
    });
    setBillNumberInput('');

    // Clear any pending timeout
    if (billNumberTimeoutRef.current) {
      clearTimeout(billNumberTimeoutRef.current);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-brand-900">Bills</h1>
            <p className="text-gray-500 mt-1 sm:mt-2 text-sm sm:text-base">Manage your bills</p>
          </div>
          <FYPicker value={selectedFY} onChange={(fy) => { setSelectedFY(fy); setPagination(prev => ({...prev, currentPage: 1})); }} />
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="w-full sm:w-auto bg-brand-500 text-white px-4 py-2.5 rounded-xl hover:bg-brand-600 transition-colors shadow-sm shadow-brand-500/25 text-base font-medium"
        >
          Add Bill
        </button>
      </div>

      {/* Filters */}
      <FilterSection
        filterValues={{...filters, billNumber: billNumberInput}}
        onCompanySelect={handleCompanyFilterSelect}
        onPartySelect={handlePartyFilterSelect}
        onBillNumberChange={handleBillNumberFilterChange}
        onClearFilters={clearFilters}
      />

      {/* Loading State */}
      {loading && (
        <>
          <div className="hidden sm:block">
            <SkeletonTable rows={5} cols={5} />
          </div>
          <div className="block sm:hidden">
            <SkeletonCards count={3} />
          </div>
        </>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-2xl border border-brand-100 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-brand-900 mb-4">
            {editingBill ? 'Edit Bill' : 'Add New Bill'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-brand-900 mb-1">
                  Company <span className="text-red-500">*</span>
                </label>
                <ApiSelect
                  type="companies"
                  value={formData.company?._id || null}
                  onChange={handleCompanySelect}
                  placeholder="Select company"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-900 mb-1">
                  Party <span className="text-red-500">*</span>
                </label>
                <ApiSelect
                  type="parties"
                  value={formData.party?._id || null}
                  onChange={handlePartySelect}
                  placeholder="Select party"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-brand-900 mb-1">
                  Bill Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="billNumber"
                  value={formData.billNumber}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-brand-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-base"
                  placeholder="Enter bill number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-900 mb-1">
                  Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-brand-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-base"
                  placeholder="Enter amount"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-brand-900 mb-1">
                  Bill Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="billDate"
                  value={formData.billDate}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-brand-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-base"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-900 mb-1">
                  Financial Year
                </label>
                <select
                  value={formData.financialYear}
                  onChange={(e) => setFormData(prev => ({ ...prev, financialYear: e.target.value }))}
                  className="w-full px-3 py-2 border border-brand-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-base"
                >
                  {getAdjacentFYs(formData.financialYear).map(fy => (
                    <option key={fy} value={fy}>FY {fy}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                className="w-full sm:w-auto bg-emerald-600 text-white px-5 py-2.5 rounded-xl hover:bg-emerald-700 transition-colors shadow-sm font-medium text-base"
              >
                {editingBill ? 'Update Bill' : 'Add Bill'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="w-full sm:w-auto bg-white text-gray-500 px-5 py-2.5 rounded-xl border border-brand-100 hover:bg-brand-50/50 transition-colors font-medium text-base"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Bills List */}
      <div className="bg-white rounded-2xl border border-brand-100 overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-brand-100">
          <h2 className="text-lg font-semibold text-brand-900">All Bills</h2>
        </div>

        {bills.length === 0 ? (
          <div className="p-4 sm:p-6 text-center text-gray-500">
            {pagination.totalItems === 0 ? 'No bills found. Add your first bill to get started.' : 'No bills match the current filters.'}
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="block sm:hidden">
              {bills.map((bill) => (
                <div key={bill._id} className="p-4 border-b border-brand-100 last:border-b-0 hover:bg-brand-50/50 transition-colors">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm font-medium text-brand-900">
                      {bill.billNumber}
                    </div>
                    <div className="text-sm font-semibold text-brand-900 tabular-nums">
                      ₹{bill.amount.toLocaleString('en-IN')}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 mb-3 space-y-0.5">
                    <div>Date: {new Date(bill.billDate).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })}</div>
                    <div>Party: {bill.party?.name || 'N/A'}</div>
                    <div>Company: {bill.company?.name || 'N/A'}</div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(bill)}
                      className="px-2.5 py-1 rounded-lg text-xs font-medium bg-brand-50 text-brand-600 hover:bg-brand-100 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(bill._id)}
                      className="px-2.5 py-1 rounded-lg text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full divide-y divide-brand-100">
                <thead className="bg-brand-50/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Party
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Bill Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-brand-100">
                  {bills.map((bill) => (
                    <tr key={bill._id} className="hover:bg-brand-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {new Date(bill.billDate).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-brand-900">
                          {bill.party?.name || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-brand-900">
                          {bill.company?.name || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-brand-900">
                          {bill.billNumber}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-brand-900 tabular-nums">
                          ₹{bill.amount.toLocaleString('en-IN')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(bill)}
                            className="px-2.5 py-1 rounded-lg text-xs font-medium bg-brand-50 text-brand-600 hover:bg-brand-100 transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(bill._id)}
                            className="px-2.5 py-1 rounded-lg text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {pagination.totalPages > 1 && (
              <div className="px-4 py-3 flex items-center justify-between border-t border-brand-100 sm:px-6">
                {/* Mobile Pagination */}
                <div className="flex sm:hidden justify-between items-center w-full">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrevPage}
                    className="relative inline-flex items-center px-3 py-2 border border-brand-100 text-sm font-medium rounded-xl text-brand-900 bg-white hover:bg-brand-50/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-500">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNextPage}
                    className="relative inline-flex items-center px-3 py-2 border border-brand-100 text-sm font-medium rounded-xl text-brand-900 bg-white hover:bg-brand-50/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>

                {/* Desktop Pagination */}
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-500">
                      Showing{' '}
                      <span className="font-medium text-brand-900">
                        {(pagination.currentPage - 1) * pagination.itemsPerPage + 1}
                      </span>{' '}
                      to{' '}
                      <span className="font-medium text-brand-900">
                        {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                      </span>{' '}
                      of{' '}
                      <span className="font-medium text-brand-900">{pagination.totalItems}</span>{' '}
                      results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-xl shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={!pagination.hasPrevPage}
                        className="relative inline-flex items-center px-3 py-2 rounded-l-xl border border-brand-100 bg-white text-sm font-medium text-gray-500 hover:bg-brand-50/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Previous
                      </button>

                      {/* Page Numbers - Show max 5 pages on mobile */}
                      {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
                        let page;
                        if (pagination.totalPages <= 5) {
                          page = i + 1;
                        } else if (pagination.currentPage <= 3) {
                          page = i + 1;
                        } else if (pagination.currentPage >= pagination.totalPages - 2) {
                          page = pagination.totalPages - 4 + i;
                        } else {
                          page = pagination.currentPage - 2 + i;
                        }

                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-colors ${
                              page === pagination.currentPage
                                ? 'z-10 bg-brand-500 border-brand-500 text-white'
                                : 'bg-white border-brand-100 text-gray-500 hover:bg-brand-50/50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}

                      <button
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={!pagination.hasNextPage}
                        className="relative inline-flex items-center px-3 py-2 rounded-r-xl border border-brand-100 bg-white text-sm font-medium text-gray-500 hover:bg-brand-50/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="Delete Bill"
        message="Are you sure you want to delete this bill? This action cannot be undone."
        onConfirm={confirmDelete}
        onCancel={() => setConfirmDialog({ isOpen: false, id: null })}
      />
    </div>
  );
};

export default Bills;
